import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, absoluteUrl } from "../lib/api";

/* NanoPanel — "piksel nano-robotlar": Tudi NE ANLATIRSA pikseller onu canlandirir.
   Konusulan cumledeki anahtar kelimeye gore sekle donusur (proton huzmesi, hucre, DNA, 日本...).
   Konuyla ilgili video varsa panelde oynatilir (sessiz, gorsel eslik). */

const PALETTE = ["#D4AF37", "#E9CF8A", "#F7F1E3", "#B58B3B", "#8A2434"]; // altın, şampanya, fildişi, bronz, bordo
const N = 1100;
const W = 320, H = 320;

function sampleShape(drawFn) {
  const off = document.createElement("canvas");
  off.width = W; off.height = H;
  const c = off.getContext("2d");
  c.fillStyle = "#fff";
  c.strokeStyle = "#fff";
  drawFn(c);
  const data = c.getImageData(0, 0, W, H).data;
  const pts = [];
  for (let y = 0; y < H; y += 4) {
    for (let x = 0; x < W; x += 4) {
      if (data[(y * W + x) * 4 + 3] > 110) pts.push({ x, y });
    }
  }
  for (let i = pts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pts[i], pts[j]] = [pts[j], pts[i]];
  }
  const out = [];
  for (let i = 0; i < N; i++) out.push(pts[i % pts.length] || { x: W / 2, y: H / 2 });
  return out;
}

const SHAPE_BUILDERS = {
  jp: (c) => { c.font = "900 150px 'Noto Sans', sans-serif"; c.textAlign = "center"; c.textBaseline = "middle"; c.fillText("日本", W / 2, H / 2 + 6); },
  dna: (c) => {
    for (let i = 0; i < 46; i++) {
      const t = (i / 45) * Math.PI * 3.1;
      const y = 26 + (i / 45) * (H - 52);
      const x1 = W / 2 + 72 * Math.sin(t);
      const x2 = W / 2 - 72 * Math.sin(t);
      c.beginPath(); c.arc(x1, y, 7, 0, 7); c.fill();
      c.beginPath(); c.arc(x2, y, 7, 0, 7); c.fill();
      if (i % 4 === 0) c.fillRect(Math.min(x1, x2), y - 2.5, Math.abs(x2 - x1), 5);
    }
  },
  cell: (c) => {
    c.lineWidth = 13; c.beginPath(); c.arc(W / 2, H / 2, 108, 0, 7); c.stroke();
    c.beginPath(); c.arc(W / 2, H / 2, 40, 0, 7); c.fill();
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      c.beginPath(); c.arc(W / 2 + Math.cos(a) * 74, H / 2 + Math.sin(a) * 74, 10, 0, 7); c.fill();
    }
  },
  beam: (c) => { // proton huzmesi: hedef daire + huzme cizgileri
    c.lineWidth = 12; c.beginPath(); c.arc(228, 160, 62, 0, 7); c.stroke();
    c.beginPath(); c.arc(228, 160, 22, 0, 7); c.fill();
    for (let i = 0; i < 5; i++) {
      const y = 96 + i * 32;
      c.fillRect(30, y - 5, 128 - Math.abs(i - 2) * 26, 10);
    }
  },
  torii: (c) => {
    c.fillRect(56, 66, 208, 20);
    c.fillRect(80, 104, 160, 15);
    c.fillRect(96, 66, 18, 196);
    c.fillRect(206, 66, 18, 196);
  },
  trust: (c) => { c.font = "900 165px 'Noto Sans', sans-serif"; c.textAlign = "center"; c.textBaseline = "middle"; c.fillText("信頼", W / 2, H / 2 + 8); },
  plus: (c) => { c.font = "900 230px 'Noto Sans', sans-serif"; c.textAlign = "center"; c.textBaseline = "middle"; c.fillText("✚", W / 2, H / 2 + 12); },
};
const SHAPE_KEYS = Object.keys(SHAPE_BUILDERS);

const KEYWORD_SHAPES = [
  [/proton|bnct|iyon|radyo|h[üu]zme|ışın|isın/i, "beam"],
  [/aşı|asi|h[üu]cre|ipsc|car[- ]?t|imm[üu]n|dendrit|k[öo]k/i, "cell"],
  [/dna|genetik|genom|piggybac|gen\b/i, "dna"],
  [/japonya|tokyo|osaka|japon|日本/i, "jp"],
  [/torii|tap[ıi]nak|kap[ıi]|ge[çc]it/i, "torii"],
  [/güven|bilim|merhaba|başla|basla|yolculuk/i, "trust"],
];

export function NanoPanel({ open, onClose, line, speaking, onOpenChat }) {
  const { t } = useLang();
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const forcedRef = useRef(null);
  const morphRef = useRef(null);
  const [videos, setVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  // Videolu makaleleri bir kez cek
  useEffect(() => {
    if (!open || videos.length) return;
    api.get("/posts").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setVideos(list.filter((p) => p.video_url));
    }).catch(() => {});
  }, [open, videos.length]);

  // Konusulan cumle -> zorunlu sekil
  useEffect(() => {
    if (!line) return;
    for (const [re, key] of KEYWORD_SHAPES) {
      if (re.test(line)) {
        forcedRef.current = key;
        if (morphRef.current) morphRef.current(key);
        // eslesen video varsa goster
        const v = videos.find((p) => re.test(`${p.tag || ""} ${p.title_tr || ""}`));
        if (v) setActiveVideo((cur) => cur || { post: v, playing: false });
        return;
      }
    }
  }, [line, videos]);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const cache = {};
    const getTargets = (key) => (cache[key] ||= sampleShape(SHAPE_BUILDERS[key]));

    let shapeKey = forcedRef.current || "jp";
    let targets = getTargets(shapeKey);
    let lastMorph = performance.now();

    if (!stateRef.current) {
      stateRef.current = Array.from({ length: N }, (_, i) => ({
        x: Math.random() * W, y: Math.random() * H,
        tx: targets[i].x, ty: targets[i].y,
        d: Math.random() * 900,
        m: performance.now(),
        c: PALETTE[i % PALETTE.length],
        s: i % 11 === 0 ? 3 : 2,
        seed: Math.random() * 1000,
      }));
    }
    const parts = stateRef.current;
    parts.forEach((p, i) => { p.tx = targets[i].x; p.ty = targets[i].y; p.m = performance.now(); });

    // Disaridan zorunlu sekil degisimi
    morphRef.current = (key) => {
      if (!SHAPE_BUILDERS[key] || key === shapeKey) return;
      shapeKey = key;
      targets = getTargets(key);
      const now = performance.now();
      parts.forEach((p, i) => { p.tx = targets[i].x; p.ty = targets[i].y; p.m = now; });
      lastMorph = now;
    };

    let raf;
    const frame = (now) => {
      ctx.clearRect(0, 0, W, H);
      const level = Math.min(1, window.__tudiLevel || 0);
      const jitter = 0.25 + level * 3.2;
      if (now - lastMorph > 5200 && !forcedRef.current) {
        const next = SHAPE_KEYS[(SHAPE_KEYS.indexOf(shapeKey) + 1) % SHAPE_KEYS.length];
        morphRef.current(next);
      }
      for (let i = 0; i < N; i++) {
        const p = parts[i];
        const k = now - p.m > p.d ? 0.055 : 0.004;
        p.x += (p.tx - p.x) * k + Math.sin(now / 900 + p.seed) * jitter * 0.28;
        p.y += (p.ty - p.y) * k + Math.cos(now / 1100 + p.seed) * jitter * 0.28;
        ctx.fillStyle = p.c;
        ctx.globalAlpha = 0.55 + level * 0.45;
        ctx.fillRect(p.x, p.y, p.s, p.s);
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Konusma bitince zorlamayi birak (serbest donguye don)
  useEffect(() => {
    if (!speaking) forcedRef.current = null;
  }, [speaking]);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          data-testid="nano-panel"
          className="fixed left-3 sm:left-6 top-1/2 -translate-y-1/2 z-[60] w-[calc(100vw-110px)] max-w-[340px]"
          initial={{ x: -380, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -380, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
        >
          <div className="rounded-3xl bg-gradient-to-b from-[#150608] via-[#1E0709] to-[#2B0A0E]/95 backdrop-blur-xl border border-[#D4AF37]/25 shadow-[0_30px_80px_rgba(20,4,6,0.65),inset_0_1px_0_rgba(212,175,55,0.15)] overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-4 pb-2.5 border-b border-[#D4AF37]/10">
              <div className="flex items-center gap-2.5">
                <span className={`w-1.5 h-1.5 rounded-full ${speaking ? "bg-[#D4AF37] animate-pulse shadow-[0_0_8px_rgba(212,175,55,0.8)]" : "bg-[#D4AF37]/30"}`} />
                <span className="text-[9px] font-heading font-semibold tracking-[0.35em] uppercase text-[#D4AF37]">{t("nano_title")}</span>
              </div>
              <button onClick={onClose} data-testid="nano-close" className="text-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors" aria-label="close">
                <X size={15} />
              </button>
            </div>

            {activeVideo?.playing ? (
              <div className="relative">
                <video
                  src={absoluteUrl(activeVideo.post.video_url)}
                  autoPlay muted loop playsInline
                  className="w-full aspect-square object-cover"
                  data-testid="nano-video"
                />
                <button
                  onClick={() => setActiveVideo(null)}
                  className="absolute top-2 right-2 text-[9px] font-bold tracking-widest uppercase bg-black/60 text-white px-2.5 py-1 rounded-full"
                  data-testid="nano-video-close"
                >
                  ✕
                </button>
              </div>
            ) : (
              <canvas ref={canvasRef} style={{ width: "100%", aspectRatio: "1" }} data-testid="nano-canvas" />
            )}

            {activeVideo && !activeVideo.playing && (
              <button
                onClick={() => setActiveVideo({ ...activeVideo, playing: true })}
                data-testid="nano-video-open"
                className="mx-4 mb-2 w-[calc(100%-32px)] flex items-center gap-2.5 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl px-3 py-2 transition-colors text-left"
              >
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8A6D1F] flex items-center justify-center shrink-0 shadow-[0_2px_8px_rgba(212,175,55,0.4)]">
                  <Play size={12} className="text-[#1A0608] ml-0.5" fill="currentColor" />
                </span>
                <span className="text-[10px] text-[#EFE6D0]/85 leading-tight line-clamp-2 font-heading">{activeVideo.post.title_tr}</span>
              </button>
            )}

            <div className="px-5 pb-5 min-h-[44px]">
              <div className="h-px w-10 bg-gradient-to-r from-[#D4AF37]/60 to-transparent mb-2.5" />
              <p className="text-[12px] leading-relaxed text-[#EFE6D0]/85 font-heading italic" data-testid="nano-subtitle">
                {line || t("nano_idle")}
              </p>
              {onOpenChat && (
                <button onClick={onOpenChat} data-testid="nano-open-chat"
                  className="mt-3 w-full bg-gradient-to-r from-[#4A0404] to-[#6B0F12] hover:from-[#5A0606] hover:to-[#7B1316] text-[#F5E7C6] text-[11px] font-heading font-bold tracking-wider py-2.5 rounded-full border border-[#D4AF37]/30 shadow-[0_4px_16px_rgba(74,4,4,0.5)] transition-all duration-300">
                  {t("nano_open_chat")} →
                </button>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
